import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../core/contexts/AuthContext';
import { useUser } from '../../core/contexts/UserContext';
import { getOsatShown } from '../../core/constants/osat';
import { usePopupBannerQuery } from './usePopupBannerQuery';
import {
  isBannerDismissed,
  markBannerDismissed,
  pruneDismissedBanners,
} from './popupBannerDismissal';
import { getPopupBannerShown } from './popupBannerSession';
import type { PopupBanner } from './popupBanner.types';

export interface PopupBannerState {
  /** The banner to display, or `null` when nothing should be shown. */
  banner: PopupBanner | null;
  /** True once the query has settled — a completed fetch, on success OR error. */
  isSettled: boolean;
  /** Records the dismissal and clears `banner`, so the caller cannot re-open it. */
  dismiss: () => void;
}

const isRenderableBanner = (value: unknown): value is PopupBanner => {
  if (value === null || typeof value !== 'object') return false;
  const b = value as Partial<PopupBanner>;
  return (
    typeof b.id === 'string' &&
    b.id.length > 0 &&
    typeof b.imageDesktop === 'string' &&
    b.imageDesktop.length > 0 &&
    typeof b.imageMobile === 'string' &&
    b.imageMobile.length > 0 &&
    typeof b.altText === 'string' &&
    b.altText.length > 0
  );
};

/**
 * Decides whether a pop-up banner should be shown, and which one.
 *
 * Gates, in order: the feature flag and a selected site (no flag ⇒ no network at all), the
 * once-per-session flag, the OSAT survey having opened this session, and this device's dismissal
 * list. Resolves the payload defensively — this feeds a blocking modal in the host chrome, so a
 * malformed response must render nothing rather than throw.
 *
 * `isSettled` reports a settled fetch rather than a successful one, so a network error cannot
 * re-arm the trigger on a later render.
 *
 * The storage gates are read imperatively, so they cannot be observed by a later render. Dismissal
 * therefore goes through `dismiss()`, which also moves state — otherwise a caller that closes the
 * banner keeps holding a non-null `banner` and re-opens it.
 */
export const usePopupBanner = (): PopupBannerState => {
  const { flags } = useAuth();
  const { siteCode } = useUser();
  const { i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;

  const enabled = flags?.ENABLE_SELF_PROMOTION_POPUP_BANNER === true && siteCode.length > 0;

  const { data, isFetched } = usePopupBannerQuery(siteCode, locale, enabled);

  const served = enabled && isRenderableBanner(data) ? data : null;

  useEffect(() => {
    if (!isFetched) return;
    pruneDismissedBanners(served ? [served.id] : []);
  }, [isFetched, served]);

  const [dismissedId, setDismissedId] = useState<string | null>(null);

  const banner = useMemo<PopupBanner | null>(() => {
    if (!served) return null;
    if (dismissedId === served.id) return null;
    if (getPopupBannerShown()) return null;
    if (getOsatShown()) return null;
    if (isBannerDismissed(served.id)) return null;
    return served;
  }, [served, dismissedId]);

  const dismiss = useCallback((): void => {
    if (!served) return;
    markBannerDismissed(served.id);
    setDismissedId(served.id);
  }, [served]);

  return { banner, isSettled: enabled ? isFetched : false, dismiss };
};
